import { upperFirst } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

import { useTranslate } from '../../../../../hooks';
import {
    cleanOgiData,
    MapControlAccordionProps,
    MSatPickInfo,
    OGILayerIds,
    OGITileFeatureProperties,
    PipelineFeatureProperties
} from '../../../../../types';
import { analytics, createHighlightPayload, formatCoordinates } from '../../../../../utils';
import { InfrastructureModalFooter } from '../../Footers';
import { InfrastructureModalHeader } from '../../Headers';
import { DataRowsModalBody, DataSkeleton, DrawerCardContainer, FeatureIDCaption } from '../../Layout';
import { useDispatch } from 'react-redux';
import { addOneMapFeatureHighlight, setSelectedOGIFeature, useGetOgiMetadataQuery } from '../../../../../reducers';
import { useOperatorCheck } from './useOperatorCheck';

export const PipelineCard = ({
    info,
    accordion,
    cardIndex,
    expanded,
    onAccordionChange,
    filterCards,
    AccordionProps
}: {
    info: MSatPickInfo<GeoJSON.Geometry, OGITileFeatureProperties>;
    accordion?: boolean;
    cardIndex?: number;
    expanded?: boolean;
    onAccordionChange?: (newExpandedAccordion: string, cardIndex: number) => void;
    filterCards: (id: string | number) => void;
    AccordionProps?: MapControlAccordionProps;
}) => {
    const t = useTranslate();
    const dispatch = useDispatch();
    const unknownOperatorName = t('emissionsMapPage.mapModals.infrastructure.unknownOperator').toUpperCase();

    const objectId = info.object.id;
    const layerId = info?.layer.id;
    const shouldFetch = layerId.includes(OGILayerIds.tileInfrastructure);

    const { data: pipelineMetadataList, isFetching: loadingState } = useGetOgiMetadataQuery(
        { ogimIds: [objectId] },
        { skip: !shouldFetch }
    );

    const pipelineMetadata = pipelineMetadataList?.[0] as PipelineFeatureProperties;

    /**
     * `infrastructure_operators` is an array because we may eventually have multiple operators over time.
     */
    const currentOperatorName =
        (pipelineMetadata?.operators && pipelineMetadata?.operators[0]?.operator_name) ?? unknownOperatorName;

    // limits OGIM status to one word so we can use it as a key to get the right translated string from locales
    const ogimStatus = pipelineMetadata?.ogim_status;
    let cleanOgimStatus = undefined;
    if (typeof ogimStatus === 'string') {
        cleanOgimStatus = cleanOgiData(ogimStatus, ' ');
    }

    // limits data source to one item
    const source = pipelineMetadata?.sources[0];

    const metadataForAnalytics = useMemo(
        () =>
            pipelineMetadata && {
                id: `${pipelineMetadata.ogim_id}`,
                latitude: info.coordinate?.[1],
                length: pipelineMetadata.details.pipe_length_km,
                longitude: info.coordinate?.[0],
                operatorName: currentOperatorName
            },
        // disabling accordion dependency linting as its required
        // eslint-disable-next-line
        [pipelineMetadata, info.coordinate, accordion, currentOperatorName]
    );
    // called from useEffect below or Accordion onChange event
    const logAnalytics = useCallback(
        () => metadataForAnalytics && analytics.openPipelineDetails(metadataForAnalytics),
        // disabling accordion dependency linting as its required
        // eslint-disable-next-line
        [metadataForAnalytics, accordion]
    );

    // tracks user opening details *not* in an accordion
    // viewing details in accordions are tracked in onChange events
    useEffect(() => {
        !accordion && logAnalytics();
    }, [pipelineMetadata, info.coordinate, accordion, logAnalytics]);

    // if the selected operators have been updated, compare and remove this ogim_id from the card list if
    // it is one of the selected operators
    useOperatorCheck({
        ogiMetaData: pipelineMetadata,
        filterCards,
        id: info.object.id
    });

    // if data has not yet loaded, displays a skeleton
    if (loadingState) {
        return <DataSkeleton />;
    }

    if (!pipelineMetadata) return <></>;

    const {
        properties: { layerName }
    } = info.object;
    const headerProps = {
        layerName: t(`emissionsMapPage.infrastructure.categories.${layerName}`) || upperFirst(layerName),
        operatorName: currentOperatorName,
        sourceDate: source?.src_date
    };

    const dataRows = [
        ...(pipelineMetadata.fac_name
            ? [
                  {
                      label: t('emissionsMapPage.mapModals.infrastructure.facilityLabel'),
                      value: pipelineMetadata.fac_name
                  }
              ]
            : []),
        ...(pipelineMetadata.ogim_status
            ? [
                  {
                      label: `${t('emissionsMapPage.mapModals.infrastructure.statusLabel')}`,
                      value: upperFirst(t(`emissionsMapPage.mapModals.common.ogimStatus.${cleanOgimStatus}`))
                  }
              ]
            : []),
        ...(pipelineMetadata.install_date
            ? [
                  {
                      label: `${t('emissionsMapPage.mapModals.infrastructure.installedLabel')}`,
                      value: `${pipelineMetadata.install_date}`
                  }
              ]
            : []),
        ...(pipelineMetadata.details.commodity
            ? [
                  {
                      label: `${t('emissionsMapPage.mapModals.infrastructure.commodityLabel')}`,
                      value: upperFirst(pipelineMetadata.details.commodity)
                  }
              ]
            : []),
        ...(pipelineMetadata.details.pipe_length_km
            ? [
                  {
                      label: `${t('emissionsMapPage.mapModals.infrastructure.lengthLabel')}`,
                      value: `${pipelineMetadata.details.pipe_length_km.toLocaleString(undefined, {
                          maximumSignificantDigits: 2
                      })} km`
                  }
              ]
            : []),
        ...(pipelineMetadata.country
            ? [
                  {
                      label: `${t('emissionsMapPage.mapModals.infrastructure.countryLabel')}`,
                      value: pipelineMetadata.country
                  }
              ]
            : []),
        ...(info.coordinate
            ? [
                  {
                      label: `${t('emissionsMapPage.mapModals.common.locationClickedLabel')}`,
                      // may want to have more significant digits for higher zoom levels
                      value: formatCoordinates(info.coordinate)
                  }
              ]
            : [])
    ];

    const featureIDCaption = `${pipelineMetadata.ogim_id}`;

    return (
        <DrawerCardContainer
            HeaderItems={<InfrastructureModalHeader {...headerProps} />}
            Body={<DataRowsModalBody dataRows={dataRows} />}
            Footer={
                <>
                    <InfrastructureModalFooter />
                    <FeatureIDCaption caption={featureIDCaption} />
                </>
            }
            accordion={accordion}
            expanded={expanded}
            AccordionProps={{
                ...AccordionProps,
                onChange: (_ev, expanded) => {
                    if (expanded) {
                        cardIndex !== undefined && onAccordionChange?.('', cardIndex);
                        logAnalytics();
                        dispatch(addOneMapFeatureHighlight(createHighlightPayload(info)));
                        dispatch(setSelectedOGIFeature(info.object));
                    } else {
                        onAccordionChange?.('', -1);
                    }
                }
            }}
        />
    );
};
